<template>
  <div class="br-8 b-1 p-8">
    <div v-if="fetchingUpcomingDates && !nextEvent" class="t">...</div>
    <div v-else>
      <div class="f-jc f-as">
        <div v-slack-format="fullText" class="t gray" />
        <i v-tooltip="tooltip" class="mi gray md-18 m-lx1">info</i>
      </div>
      <div v-if="statusText" class="f-as t-sm gray m-tx1">
        <i v-if="saveStatus === 'saving'" class="mi anim-spin purple md-16 m-r">refresh</i>
        <i v-else-if="saveStatus === 'saved'" class="mi green md-16 m-r">check</i>
        <span>{{ statusText }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, inject, watch } from 'vue';
  import { useStore } from 'vuex';

  const $i18n = inject('$i18n');
  const { state, getters, dispatch } = useStore();

  const props = defineProps({
    milestoneType: { type: String, required: true },
  });
  const pointsNoun = $i18n.t('shoutouts.point', { count: 2 });

  const config = computed(() => state.bonusMilestoneConfigs[props.milestoneType]);
  const nextEvent = ref(null);
  const pointsForEvent = (event) => getters['bonusMilestoneConfigs/pointsForEvent'](props.milestoneType, event.ordinal);

  // Fetching
  const fetchingUpcomingDates = ref(false);
  const nextPage = ref(1);
  const resetForNextFetch = () => {
    fetchingUpcomingDates.value = false;
    nextPage.value = 1;
  };
  const fetchUpcomingDates = () => {
    fetchingUpcomingDates.value = true;
    dispatch('bonusMilestoneConfigs/fetchUpcomingDates', { milestone_types: props.milestoneType, page: nextPage.value })
      .then((data) => {
        // Take the first upcoming schedule for which pointsForEvent is not 0. We may not find one, either because
        // the controller filtered too many out or because everything here is worth 0 points under these amounts.
        const found = data.list.find(event => pointsForEvent(event) !== 0 && !event.walletInactive);
        if (!found && data.nextPage) {
          nextPage.value += 1;
          fetchUpcomingDates();
          return;
        }
        nextEvent.value = found || null;
        resetForNextFetch();
      })
      .catch(resetForNextFetch);
  };

  // Watchers
  // Several things might make us want to trigger a refresh of this copy. Assemble them into a string "key" and detect changes to it.
  const watchKey = computed(() => {
    const key = {};
    const { hrisCategories, enrollmentRulesPending } = state.hrisRecipientsRuleSelector;
    key.pending = enrollmentRulesPending;
    if (hrisCategories) key.hrisCategories = hrisCategories.filter(c => c.active).map(c => c.hris_value);
    if (props.milestoneType === 'anniversary') {
      key.amountMode = config.value.amount_mode;
      key.pointsByYear = config.value.points_by_year;
    }
    return JSON.stringify(key);
  });
  watch(watchKey, () => {
    if (state.hrisRecipientsRuleSelector.enrollmentRulesPending) return;
    fetchUpcomingDates();
  }, { immediate: true });

  // Rendering
  const eventSentence = (e) => `next scheduled event will send a message with ${pointsForEvent(e)} ${pointsNoun} to ${e.user} on ${e.date} at 9am ${e.timeZone}`;
  const fullText = computed(() => {
    const e = nextEvent.value;
    if (!config.value.active) {
      if (e) return `When you turn this on, the ${eventSentence(e)}.`;
      return `Nobody is currently scheduled to receive milestone ${pointsNoun}. Please check the Recipients section below.`;
    }
    if (e) return `Automated ${props.milestoneType} ${pointsNoun} are on. Your ${eventSentence(e)}.`;

    let copy = `Automated ${props.milestoneType} ${pointsNoun} are on, but there is currently *nobody scheduled to receive any*`;
    if (config.value.amount_mode === 'custom_only') {
      // Anniversaries only for particular milestone years. This might mean that there are valid schedules, but nobody upcoming this year.
      copy += ' this year';
    }
    return `${copy}. Please check the Recipients section below.`;
  });
  const saveStatus = computed(() => state.bonusMilestoneConfigs.saveStatus[props.milestoneType]);
  const statusText = computed(() => {
    if (saveStatus.value === 'saving') return 'Saving settings...';
    return saveStatus.value === 'saved' || !config.value.active ? 'These settings are saved.' : null;
  });
  const tooltip = {
    content: 'You can see all scheduled events in the Upcoming Events tab. If a scheduled points recipient is not currently an '
      + '<a href="https://help.donut.ai/en/articles/12735072-active-users-and-billing-in-donut" target="_blank" class="gray-purple">'
      + 'active Donut user</a> they will become active when their points are delivered.',
    // eslint-disable-next-line no-restricted-syntax -- content is a hard-coded string
    html: true,
    popperTriggers: ['hover', 'focus'],
  };
</script>
